import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit3, Beaker, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import ExportButton from "@/components/ExportButton";
import { api } from "@/lib/api";
import {
  formatDateEU, todayISO, generateBatchNumber, PRODUCT_BATCH_PREFIX,
} from "@/lib/constants";

const blank = () => ({
  company_name: "",
  lead_id: null,
  contact_person: "",
  date_delivered: todayISO(),
  products_included: [],
  batches: [],
  linked_batch_ids: [],
  quantity_delivered: "",
  sample_notes: "",
  follow_up_date: "",
  feedback: "",
  outcome: "Pending Feedback",
});

const OUTCOME_COLORS = {
  "Pending Feedback": "#F4A261",
  "Positive Feedback": "#2A9D8F",
  "Needs Adjustment": "#E76F51",
  "Interested": "#E85D04",
  "Ordered": "#2A9D8F",
  "Not Interested": "#6C757D",
};

export default function Samples() {
  const [samples, setSamples] = useState([]);
  const [leads, setLeads] = useState([]);
  const [productionBatches, setProductionBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  const load = async () => {
    const [s, l, m, pb, pr, inv] = await Promise.all([
      api.listSamples(), api.listLeads(), api.getMeta(), api.listProductionBatches(),
      api.listProducts(), api.listInventory(),
    ]);
    setSamples(s);
    setLeads(l);
    setMeta(m);
    setProductionBatches(pb);
    setProducts(pr);
    setInventory(inv);
  };

  useEffect(() => { load(); }, []);

  // Resolve a sample-sized SKU for a given product name (prefer "Sample" category, then 100ml format).
  const resolveSampleSku = (productName) => {
    if (!productName) return null;
    const pool = products.filter((p) => p.name === productName);
    return (
      pool.find((p) => (p.category || "").toLowerCase() === "sample") ||
      pool.find((p) => /100\s*ml/i.test(p.format || "")) ||
      pool[0] ||
      null
    );
  };

  const stockFor = (productId) => {
    const row = inventory.find((i) => i.product_id === productId);
    return row ? Number(row.stock_units || 0) : 0;
  };

  const sampleableProducts = useMemo(
    () => (meta ? meta.products.filter((p) => PRODUCT_BATCH_PREFIX[p]) : []),
    [meta]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(blank());
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      ...blank(), ...s,
      batches: s.batches || [],
      products_included: s.products_included || [],
      linked_batch_ids: s.linked_batch_ids || [],
    });
    setOpen(true);
  };

  const toggleLinkedBatch = (pbId) => {
    setForm((f) => {
      const has = f.linked_batch_ids.includes(pbId);
      const linked = has
        ? f.linked_batch_ids.filter((x) => x !== pbId)
        : [...f.linked_batch_ids, pbId];
      // Sync the textual batches list to mirror linked production batches
      let batches = [...(f.batches || [])];
      if (has) {
        const pb = productionBatches.find((p) => p.id === pbId);
        if (pb) batches = batches.filter((b) => b.batch_number !== pb.batch_id);
      } else {
        const pb = productionBatches.find((p) => p.id === pbId);
        if (pb && !batches.find((b) => b.batch_number === pb.batch_id)) {
          const sku = resolveSampleSku(pb.product);
          batches.push({
            product: pb.product,
            batch_number: pb.batch_id,
            quantity: "500ml",
            product_id: sku?.id || null,
            units: 1,
          });
        }
      }
      // Sync products_included with linked batches' products too
      const productsFromLinks = productionBatches
        .filter((p) => linked.includes(p.id))
        .map((p) => p.product);
      const productsIncluded = Array.from(new Set([...(f.products_included || []), ...productsFromLinks]));
      return { ...f, linked_batch_ids: linked, batches, products_included: productsIncluded };
    });
  };

  const toggleProduct = (p) => {
    setForm((f) => {
      const has = f.products_included.includes(p);
      const productsList = has
        ? f.products_included.filter((x) => x !== p)
        : [...f.products_included, p];
      // Auto-add a batch row for newly selected sampleable products
      let batches = [...f.batches];
      if (!has && PRODUCT_BATCH_PREFIX[p]) {
        const sku = resolveSampleSku(p);
        batches.push({
          product: p,
          batch_number: generateBatchNumber(p, f.date_delivered, 1),
          quantity: "500ml",
          product_id: sku?.id || null,
          units: 1,
        });
      } else if (has) {
        batches = batches.filter((b) => b.product !== p);
      }
      return { ...f, products_included: productsList, batches };
    });
  };

  const updateBatch = (idx, field, value) => {
    setForm((f) => {
      const batches = [...f.batches];
      batches[idx] = { ...batches[idx], [field]: value };
      return { ...f, batches };
    });
  };

  const save = async () => {
    if (!form.company_name) return toast.error("Company is required");
    // Validate units > 0 if a product_id is linked (we'll decrement inventory)
    for (const b of form.batches || []) {
      if (b.product_id && (!b.units || Number(b.units) <= 0)) {
        return toast.error(`Set units > 0 for ${b.product} (SKU is linked, inventory will be decremented)`);
      }
    }
    try {
      if (editing) {
        await api.updateSample(editing.id, form);
        toast.success("Sample updated · inventory adjusted");
      } else {
        await api.createSample(form);
        toast.success("Sample created · inventory decremented");
      }
      setOpen(false);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to save sample";
      toast.error(msg);
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete sample for "${s.company_name}"?`)) return;
    await api.deleteSample(s.id);
    toast.success("Sample deleted");
    load();
  };

  if (!meta) return <div className="text-sm text-[#6C757D]">Loading…</div>;

  return (
    <div data-testid="page-samples">
      <PageHeader
        overline="Field Activity"
        title="Sample Tracking"
        subtitle="Every sauce sample, every batch, every restaurant."
        actions={
          <div className="flex gap-2">
            <ExportButton module="samples" />
            <Button
              onClick={openCreate}
              data-testid="add-sample-btn"
              className="bg-[#E85D04] hover:bg-[#c94b03] text-white"
            >
              <Plus size={16} className="mr-2" /> Record Sample
            </Button>
          </div>
        }
      />

      {samples.length === 0 ? (
        <EmptyState
          icon={Beaker}
          title="No samples yet"
          description="Record your first sample delivery — batch numbers, quantities and feedback."
          action={
            <Button onClick={openCreate} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="empty-add-sample">
              <Plus size={16} className="mr-2" /> Record Sample
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {samples.map((s) => (
            <div key={s.id} className="card-soft card-soft-hover p-5" data-testid={`sample-card-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#6C757D] font-bold">
                    Delivered · {formatDateEU(s.date_delivered)}
                  </div>
                  <h3 className="font-display font-semibold text-lg text-[#222]">{s.company_name}</h3>
                  <div className="text-xs text-[#6C757D]">{s.contact_person || "—"}</div>
                </div>
                <span
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider"
                  style={{
                    backgroundColor: `${OUTCOME_COLORS[s.outcome] || "#6C757D"}1f`,
                    color: OUTCOME_COLORS[s.outcome] || "#6C757D",
                  }}
                  data-testid={`sample-outcome-${s.id}`}
                >
                  {s.outcome}
                </span>
              </div>

              {s.batches?.length > 0 && (
                <div className="mt-4 grid grid-cols-1 gap-2">
                  {s.batches.map((b, i) => {
                    const linked = productionBatches.find((pb) => pb.batch_id === b.batch_number);
                    return (
                      <div
                        key={i}
                        className="flex items-center justify-between bg-[#F7F1E8] rounded-lg px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Tag size={14} className="text-[#E85D04] shrink-0" />
                          <div className="truncate">
                            <div className="text-sm font-semibold text-[#222] truncate">{b.product}</div>
                            <div className="text-[11px] font-mono text-[#1F4D3A] flex items-center gap-1">
                              {b.batch_number}
                              {linked && (
                                <span
                                  className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#2A9D8F]/15 text-[#2A9D8F] font-bold"
                                  title="Linked to a production batch record"
                                >
                                  traced
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-[#6C757D] shrink-0 ml-2">
                          {b.units ? `${b.units}u` : (b.quantity || "—")}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Follow Up</div>
                  <div className="text-[#1F4D3A] font-semibold">{formatDateEU(s.follow_up_date)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Quantity</div>
                  <div>{s.quantity_delivered || "—"}</div>
                </div>
              </div>

              {(s.sample_notes || s.feedback) && (
                <div className="mt-3 text-sm text-[#222] space-y-1">
                  {s.sample_notes && <p><span className="text-[#6C757D] text-xs uppercase tracking-wider mr-1">Notes:</span>{s.sample_notes}</p>}
                  {s.feedback && <p><span className="text-[#2A9D8F] text-xs uppercase tracking-wider mr-1">Feedback:</span>{s.feedback}</p>}
                </div>
              )}

              <div className="mt-3 flex justify-end gap-1 border-t border-[#E5E5E5] pt-3">
                <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-sample-${s.id}`}>
                  <Edit3 size={16} className="mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => del(s)} className="text-[#E76F51] hover:bg-[#E76F51]/10" data-testid={`delete-sample-${s.id}`}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sample-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit Sample" : "Record Sample Delivery"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Company / Restaurant *">
              <Select
                value={form.lead_id || "__none"}
                onValueChange={(v) => {
                  if (v === "__none") {
                    setForm({ ...form, lead_id: null });
                  } else {
                    const lead = leads.find((l) => l.id === v);
                    if (lead) {
                      setForm({
                        ...form,
                        lead_id: lead.id,
                        company_name: lead.company_name,
                        contact_person: lead.contact_person || "",
                      });
                    }
                  }
                }}
              >
                <SelectTrigger data-testid="input-sample-lead"><SelectValue placeholder="Pick existing lead…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Not linked —</SelectItem>
                  {leads.map((l) => (<SelectItem key={l.id} value={l.id}>{l.company_name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Input
                className="mt-2"
                placeholder="Or type company name"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                data-testid="input-sample-company"
              />
            </Field>
            <Field label="Contact Person">
              <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                data-testid="input-sample-contact" />
            </Field>
            <Field label="Date Delivered">
              <Input type="date" value={form.date_delivered || ""}
                onChange={(e) => setForm({ ...form, date_delivered: e.target.value })}
                data-testid="input-sample-date" />
            </Field>
            <Field label="Follow Up Date">
              <Input type="date" value={form.follow_up_date || ""}
                onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                data-testid="input-sample-followup" />
            </Field>
            <Field label="Outcome">
              <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                <SelectTrigger data-testid="input-sample-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.sample_outcomes.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Quantity Delivered (free text)">
              <Input value={form.quantity_delivered} onChange={(e) => setForm({ ...form, quantity_delivered: e.target.value })}
                placeholder="e.g. 2 x 500ml"
                data-testid="input-sample-quantity" />
            </Field>

            <Field label="Linked Production Batches" className="sm:col-span-2">
              {productionBatches.length === 0 ? (
                <div className="text-xs text-[#6C757D] bg-[#F7F1E8] rounded-lg p-3">
                  No production batches yet. Add some in <span className="font-semibold text-[#E85D04]">Production</span> to enable traceability.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto pr-1">
                  {productionBatches.map((pb) => (
                    <label
                      key={pb.id}
                      className="flex items-center gap-2 text-sm bg-[#F7F1E8]/60 hover:bg-[#F7F1E8] rounded-lg px-3 py-2 cursor-pointer"
                      data-testid={`link-batch-${pb.id}`}
                    >
                      <Checkbox
                        checked={form.linked_batch_ids.includes(pb.id)}
                        onCheckedChange={() => toggleLinkedBatch(pb.id)}
                      />
                      <div className="min-w-0">
                        <div className="font-mono text-xs text-[#E85D04] font-bold truncate">{pb.batch_id}</div>
                        <div className="text-[11px] text-[#6C757D] truncate">{pb.product}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </Field>

            <Field label="Products Included" className="sm:col-span-2">
              <div className="grid grid-cols-2 gap-2">
                {sampleableProducts.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.products_included.includes(p)}
                      onCheckedChange={() => toggleProduct(p)}
                      data-testid={`sample-product-${p}`}
                    />
                    {p}
                  </label>
                ))}
              </div>
            </Field>

            {form.batches.length > 0 && (
              <Field label="Batch Numbers & Units (auto-decrements inventory)" className="sm:col-span-2">
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 items-center text-[10px] uppercase tracking-wider text-[#6C757D] font-bold px-1">
                    <div className="col-span-4">Product</div>
                    <div className="col-span-4">Batch #</div>
                    <div className="col-span-2 text-center">Units</div>
                    <div className="col-span-2 text-right">Stock</div>
                  </div>
                  {form.batches.map((b, i) => {
                    const sku = b.product_id ? products.find((p) => p.id === b.product_id) : resolveSampleSku(b.product);
                    const pid = b.product_id || sku?.id || null;
                    const available = pid ? stockFor(pid) : 0;
                    const requested = Number(b.units || 0);
                    const low = pid && requested > available;
                    return (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-4 text-sm">
                          <div className="font-semibold text-[#222] truncate">{b.product}</div>
                          {sku && (
                            <div className="text-[10px] text-[#6C757D] truncate">
                              SKU: {sku.format || sku.sku || "—"}
                            </div>
                          )}
                        </div>
                        <Input
                          className="col-span-4 font-mono text-xs"
                          value={b.batch_number}
                          onChange={(e) => updateBatch(i, "batch_number", e.target.value)}
                          data-testid={`batch-input-${i}`}
                        />
                        <Input
                          className="col-span-2 text-center"
                          type="number"
                          min="0"
                          step="1"
                          value={b.units ?? ""}
                          onChange={(e) => {
                            const n = e.target.value === "" ? "" : Number(e.target.value);
                            updateBatch(i, "units", n);
                            // also auto-link product_id if missing
                            if (!b.product_id && sku) updateBatch(i, "product_id", sku.id);
                          }}
                          placeholder="0"
                          data-testid={`batch-units-${i}`}
                        />
                        <div
                          className={`col-span-2 text-right text-xs font-semibold ${low ? "text-[#D62828]" : "text-[#2A9D8F]"}`}
                          data-testid={`batch-stock-${i}`}
                        >
                          {pid ? `${available} avail` : "—"}
                          {low && <div className="text-[10px] font-bold">Not enough!</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Field>
            )}

            <Field label="Sample Notes" className="sm:col-span-2">
              <Textarea rows={2} value={form.sample_notes} onChange={(e) => setForm({ ...form, sample_notes: e.target.value })}
                data-testid="input-sample-notes" />
            </Field>
            <Field label="Feedback" className="sm:col-span-2">
              <Textarea rows={2} value={form.feedback} onChange={(e) => setForm({ ...form, feedback: e.target.value })}
                data-testid="input-sample-feedback" />
            </Field>
          </div>

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-sample">Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-sample">
              {editing ? "Save Changes" : "Create Sample"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
